import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Shield, TrendingUp, AlertTriangle, Calculator, ArrowRight, 
  Clock, FileText, RefreshCw, Check, ChevronRight, Zap,
  Eye, Bell, Languages, Lock, Users, Star, Menu, X
} from 'lucide-react';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Fetch waitlist count
    axios.get(`${BACKEND_URL}/api/waitlist/count`)
      .then(res => setWaitlistCount(res.data.count))
      .catch(() => {});
  }, []);

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/waitlist/join`, {
        email,
        name,
        source: 'landing_page'
      });
      
      toast.success(res.data.message);
      
      // Redirect to calculator after successful signup
      setTimeout(() => {
        navigate('/calculator');
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo size="small" className="[&_span]:text-white [&_.text-gray-400]:text-slate-400" />
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <Link 
                to="/calculator" 
                className="text-slate-300 hover:text-white transition-colors text-sm"
              >
                Calculator
              </Link>
              <Link 
                to="/about" 
                className="text-slate-300 hover:text-white transition-colors text-sm"
              >
                About Us
              </Link>
              <a 
                href="/login" 
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Login
              </a>
              <a 
                href="#waitlist" 
                className="px-4 py-2 text-sm font-medium bg-amber-500 text-slate-900 rounded-lg hover:bg-amber-400 transition-colors"
              >
                Join Waitlist
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-700">
              <div className="flex flex-col gap-3">
                <Link 
                  to="/calculator" 
                  className="text-slate-300 hover:text-white transition-colors text-sm py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Calculator
                </Link>
                <Link 
                  to="/about" 
                  className="text-slate-300 hover:text-white transition-colors text-sm py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About Us
                </Link>
                <a 
                  href="/login" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-white transition-colors text-sm py-2"
                >
                  Login
                </a>
                <a 
                  href="#waitlist" 
                  className="inline-block text-center px-4 py-2 text-sm font-medium bg-amber-500 text-slate-900 rounded-lg hover:bg-amber-400 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Join Waitlist
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400 text-sm">
                <Shield size={14} />
                <span>Trusted by families across India</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Don't let your pledged assets become 
                <span className="text-amber-400"> someone else's profit.</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed">
                Your Gold, Shares, and Mutual Funds are scattered across banks and brokers. 
                <span className="text-white font-medium"> Stop flying blind.</span>
              </p>
              
              <p className="text-slate-400">
                Track liabilities, monitor real-time Loan-to-Value (LTV) risk, and protect your family's Net Worth in one agile dashboard.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="#waitlist"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-amber-500 text-slate-900 font-semibold rounded-xl hover:bg-amber-400 transition-all transform hover:scale-105 shadow-lg shadow-amber-500/25"
                >
                  Request Early Access
                  <ArrowRight size={20} />
                </a>
                <Link 
                  to="/calculator"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-slate-600 text-white rounded-xl hover:bg-slate-800 transition-colors"
                >
                  <Calculator size={20} />
                  Try Calculator Free
                </Link>
              </div>

              <p className="text-sm text-slate-400 flex items-center gap-2">
                <span className="text-amber-400">🎁</span>
                Join now to unlock our "True Cost" Interest Calculator immediately.
              </p>
            </div>

            {/* Hero Visual - Dashboard Preview */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-blue-500/20 blur-3xl rounded-full"></div>
              <div className="relative bg-slate-800/80 backdrop-blur border border-slate-700 rounded-2xl p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-slate-400 text-sm">Live Dashboard</span>
                  </div>
                  <Shield className="text-green-500" size={24} />
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-700/50 rounded-xl p-4">
                    <p className="text-slate-400 text-xs mb-1">Total Assets</p>
                    <p className="text-2xl font-bold text-white">₹1.2 Cr</p>
                    <p className="text-green-400 text-xs">+12.5% this year</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-4">
                    <p className="text-slate-400 text-xs mb-1">Total Liability</p>
                    <p className="text-2xl font-bold text-white">₹45 L</p>
                    <p className="text-amber-400 text-xs">LTV: 37.5%</p>
                  </div>
                </div>

                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
                  <Shield className="text-green-500" size={32} />
                  <div>
                    <p className="text-green-400 font-semibold">All Assets Safe</p>
                    <p className="text-slate-400 text-sm">LTV well within limits</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The "Blind Spot" in Indian Wealth
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              You are <span className="text-white font-medium">Asset Rich</span>. But if you're managing secured loans on sticky notes or mental math, you are <span className="text-amber-400 font-medium">Operationally Poor</span>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-8 hover:border-red-500/50 transition-colors">
              <div className="w-14 h-14 bg-red-500/10 rounded-xl flex items-center justify-center mb-6">
                <FileText className="text-red-400" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">The Fragmented Mess</h3>
              <p className="text-slate-400">
                Pawn broker receipts in the drawer. MTF positions in the trading app. Bank loans in the email. <span className="text-white">No single view.</span>
              </p>
            </div>

            <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-8 hover:border-amber-500/50 transition-colors">
              <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="text-amber-400" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">The Volatility Risk</h3>
              <p className="text-slate-400">
                Markets crash. Gold prices fluctuate. Without real-time alerts, you risk <span className="text-amber-400 font-medium">Margin Calls or Auctions</span>.
              </p>
            </div>

            <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-8 hover:border-blue-500/50 transition-colors">
              <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mb-6">
                <Calculator className="text-blue-400" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">The Interest Trap</h3>
              <p className="text-slate-400">
                Do you pay % per annum or Rupees per month? We normalize the math so you <span className="text-white">see the truth</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Your Wealth, <span className="text-amber-400">Under Control</span>
            </h2>
            <p className="text-lg text-slate-400">Three powerful features to protect what you've built</p>
          </div>

          <div className="space-y-16">
            {/* Feature 1 */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm mb-4">
                  <span className="font-bold">01</span>
                  <span>Command Center</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                  Sync Offline & Online
                </h3>
                <p className="text-slate-400 mb-6">
                  Upload a photo of your local pawn ticket or input your digital LAS details. See your total liability vs. total asset value in a single <span className="text-white font-medium">"Net Equity" view</span>.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-300">
                    <Check className="text-green-500" size={20} />
                    Track gold, shares, mutual funds in one place
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <Check className="text-green-500" size={20} />
                    Support for local pawn brokers & banks
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <Check className="text-green-500" size={20} />
                    Family-wide consolidated view
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2 bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
                <div className="aspect-video bg-gradient-to-br from-blue-500/10 to-slate-800 rounded-xl flex items-center justify-center">
                  <Eye className="text-blue-400" size={64} />
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
                <div className="aspect-video bg-gradient-to-br from-amber-500/10 to-slate-800 rounded-xl flex items-center justify-center">
                  <Bell className="text-amber-400" size={64} />
                </div>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400 text-sm mb-4">
                  <span className="font-bold">02</span>
                  <span>Agile Guardian</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                  Live Risk Monitoring
                </h3>
                <p className="text-slate-400 mb-6">
                  We track live Gold prices and Stock market moves against your specific loans.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-300">
                    <Zap className="text-amber-500" size={20} />
                    Market Crash? We alert you instantly.
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <Zap className="text-amber-500" size={20} />
                    LTV breaching 70%? We tell you before the bank does.
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <Zap className="text-amber-500" size={20} />
                    Daily & weekly risk summaries
                  </li>
                </ul>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-sm mb-4">
                  <span className="font-bold">03</span>
                  <span>Vernacular Engine</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                  We Speak Your Money
                </h3>
                <p className="text-slate-400 mb-6">
                  Input interest in Rupees, Percent, or EMI. We convert it all into a standardized risk score. <span className="text-white font-medium">No more mental gymnastics.</span>
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-300">
                    <Languages className="text-green-500" size={20} />
                    Rupees per month → Annual % conversion
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <Languages className="text-green-500" size={20} />
                    Compare local vs bank loan costs
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <Languages className="text-green-500" size={20} />
                    Telugu, Hindi, English support
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2 bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
                <div className="aspect-video bg-gradient-to-br from-green-500/10 to-slate-800 rounded-xl flex items-center justify-center">
                  <Languages className="text-green-400" size={64} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Story Section - Simplified for Landing Page */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <Logo size="hero" className="[&_span]:text-white [&_.text-gray-400]:text-slate-400 [&_.text-gray-500]:text-slate-400" />
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            "It's Pledged. But it's still <span className="text-amber-400">Yours</span>."
          </h2>
          
          <p className="text-lg text-slate-300 mb-8">
            In Telugu, <span className="text-amber-400 font-semibold">Maa Dabbu</span> means "Our Money." <span className="text-white font-medium">It's time you treated it that way.</span>
          </p>
          
          <Link 
            to="/about"
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors"
          >
            Learn more about our story
            <ChevronRight size={18} />
          </Link>
        </div>
      </section>

      {/* Calculator Hook Section */}
      <section id="waitlist" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-amber-500/10 via-slate-800 to-blue-500/10 border border-slate-700 rounded-3xl p-8 sm:p-12">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full text-amber-400 text-sm font-medium mb-6">
                <Calculator size={18} />
                <span>Free Tool Available Now</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Can't wait for the app? <span className="text-amber-400">Start calculating today.</span>
              </h2>
              
              <p className="text-slate-400 max-w-xl mx-auto">
                Join the waitlist and get free access to our web-based "Vaddi Converter" & Risk Calculator. See exactly how much your local loan is costing you in annual percentage terms.
              </p>
            </div>

            <form onSubmit={handleJoinWaitlist} className="max-w-md mx-auto space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Your Name (Optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-5 py-4 bg-slate-800/80 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-5 py-4 bg-slate-800/80 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-4 bg-amber-500 text-slate-900 font-semibold rounded-xl hover:bg-amber-400 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <RefreshCw className="animate-spin" size={20} />
                ) : (
                  <>
                    Get Access & Join Waitlist
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>

            {waitlistCount > 10 && (
              <p className="text-center text-slate-400 text-sm mt-6">
                <Users className="inline mr-2" size={16} />
                Join {waitlistCount}+ others on the waitlist
              </p>
            )}

            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <Lock size={14} />
                No spam, ever
              </span>
              <span className="flex items-center gap-2">
                <Shield size={14} />
                Data encrypted
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Logo size="small" showText={false} />
              <div>
                <p className="text-white font-semibold">Maa Dabbu</p>
                <p className="text-slate-500 text-xs italic">By Buddi</p>
                <p className="text-slate-400 text-sm">Your Wealth. Your Control.</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <Link to="/calculator" className="hover:text-white transition-colors">Calculator</Link>
              <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
              <a href="/login" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Login</a>
              <a href="#waitlist" className="hover:text-white transition-colors">Join Waitlist</a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-800 text-center">
            <p className="text-slate-500 text-sm flex items-center justify-center gap-2">
              <Lock size={14} />
              We take data privacy seriously. Your financial data is encrypted and never shared with lenders.
            </p>
            <p className="text-slate-600 text-sm mt-4">
              © Being Ultimate
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
